/**
 * `AigorContext` — contesto React per controllare l'apertura del Drawer di AIgor.
 *
 * Il Drawer che conduce la valutazione AI (quiz) è montato una sola volta nel
 * `Layout` (task 7.4), ma il comando di avvio ("Valuta con AIgor") vive accanto
 * a ciascuna Skill in `SkillList`/`AssessmentWizard`. Questo contesto disaccoppia
 * i due lati: i trigger chiamano {@link AigorContextValue.open} passando lo
 * `skillId` (ed eventualmente nome della Skill e nome utente per personalizzare
 * la conversazione), mentre il Drawer osserva `openSkillId` per aprirsi e
 * avviare il quiz sulla Skill corretta (Req. 3.1).
 *
 * Espone lo stato minimo indispensabile: quale Skill è attualmente "aperta" nel
 * Drawer (`openSkillId`, `null` = chiuso), il nome della Skill e dell'utente
 * (usati nei messaggi di benvenuto/chiusura della chat), più le azioni
 * `open(skillId, meta?)` / `close()`. Lo stato del quiz vero e proprio (domande,
 * timer, invio) resta di competenza dell'hook `useAiQuiz`, usato dentro il Drawer.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Livello } from '../shared/dto';

/** Metadati opzionali per personalizzare la conversazione con AIgor. */
export interface AigorOpenMeta {
  /** Nome leggibile della Skill in valutazione. */
  skillName?: string;
  /** Descrizione della Skill (mostrata nell'onboarding del quiz), se presente. */
  skillDescription?: string;
  /** Nome dell'utente che sta svolgendo il quiz. */
  userName?: string;
  /**
   * Livello AI già stimato per questa Skill, se presente. Quando non è `null`
   * l'onboarding di AIgor avvisa l'utente che la Skill è già stata valutata e
   * gli chiede se vuole avviare una nuova valutazione (che sovrascrive quella
   * precedente).
   */
  existingAiLevel?: Livello | null;
}

/** Valore esposto dal contesto di AIgor. */
export interface AigorContextValue {
  /** Skill attualmente aperta nel Drawer, o `null` se il Drawer è chiuso. */
  openSkillId: string | null;
  /** Nome della Skill aperta nel Drawer, o `null`. */
  openSkillName: string | null;
  /** Descrizione della Skill aperta nel Drawer, o `null`. */
  openSkillDescription: string | null;
  /** Nome dell'utente per la sessione aperta, o `null`. */
  openUserName: string | null;
  /**
   * Livello AI già stimato per la Skill aperta, o `null` se la Skill non è
   * ancora stata valutata dall'AI.
   */
  openExistingAiLevel: Livello | null;
  /** Apre il Drawer per la Skill indicata (avvia la valutazione AI). */
  open: (skillId: string, meta?: AigorOpenMeta) => void;
  /** Chiude il Drawer. */
  close: () => void;
}

const AigorContext = createContext<AigorContextValue | null>(null);

interface AigorProviderProps {
  children: ReactNode;
}

/**
 * `AigorProvider` — provider del contesto di AIgor. Va montato attorno alla
 * porzione di app che contiene sia i trigger (per Skill) sia il Drawer
 * (tipicamente il `Layout`, task 7.4).
 */
export function AigorProvider({ children }: AigorProviderProps) {
  const [openSkillId, setOpenSkillId] = useState<string | null>(null);
  const [openSkillName, setOpenSkillName] = useState<string | null>(null);
  const [openSkillDescription, setOpenSkillDescription] = useState<string | null>(
    null,
  );
  const [openUserName, setOpenUserName] = useState<string | null>(null);
  const [openExistingAiLevel, setOpenExistingAiLevel] = useState<Livello | null>(
    null,
  );

  const open = useCallback((skillId: string, meta?: AigorOpenMeta) => {
    setOpenSkillId(skillId);
    setOpenSkillName(meta?.skillName ?? null);
    setOpenSkillDescription(meta?.skillDescription ?? null);
    setOpenUserName(meta?.userName ?? null);
    setOpenExistingAiLevel(meta?.existingAiLevel ?? null);
  }, []);

  const close = useCallback(() => {
    setOpenSkillId(null);
    setOpenSkillName(null);
    setOpenSkillDescription(null);
    setOpenUserName(null);
    setOpenExistingAiLevel(null);
  }, []);

  const value = useMemo<AigorContextValue>(
    () => ({
      openSkillId,
      openSkillName,
      openSkillDescription,
      openUserName,
      openExistingAiLevel,
      open,
      close,
    }),
    [
      openSkillId,
      openSkillName,
      openSkillDescription,
      openUserName,
      openExistingAiLevel,
      open,
      close,
    ],
  );

  return <AigorContext.Provider value={value}>{children}</AigorContext.Provider>;
}

/**
 * `useAigor` — accesso al contesto di AIgor. Deve essere usato sotto un
 * {@link AigorProvider}, altrimenti lancia un errore esplicito.
 */
export function useAigor(): AigorContextValue {
  const ctx = useContext(AigorContext);
  if (ctx === null) {
    throw new Error('useAigor deve essere usato dentro un <AigorProvider>.');
  }
  return ctx;
}
